import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = "TIKA";
const SITE_URL = "https://tikaculture.com";
const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1200";

/**
 * SEO Component for TIKA
 * Handles meta tags, Open Graph, Twitter Cards, and Schema.org
 */
export const SEO = ({
  title,
  description,
  image,
  url,
  type = "website",
  event = null,
  article = null,
  noIndex = false
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Événements Culturels Hip-Hop, Afrobeat & Dancehall`;
  const metaDescription = description || "Découvrez et réservez vos billets pour les meilleurs événements Hip-Hop, Afrobeat, R&B et Dancehall. Concerts, soirées et festivals culturels à Paris et en France.";
  const metaImage = image || DEFAULT_IMAGE;
  const canonicalUrl = url || SITE_URL;

  // Schema.org structured data for Event
  const eventSchema = event ? {
    "@context": "https://schema.org",
    "@type": "MusicEvent",
    "name": event.title,
    "description": event.description || `Concert ${event.artist} - ${event.category}`,
    "image": event.image,
    "startDate": event.date,
    "location": {
      "@type": "Place",
      "name": event.venue,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": event.location
      }
    },
    "performer": {
      "@type": "MusicGroup",
      "name": event.artist
    },
    "offers": {
      "@type": "Offer",
      "price": event.price,
      "priceCurrency": "EUR",
      "availability": event.sold_out ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
      "url": `${SITE_URL}/event/${event.id}`
    },
    "organizer": {
      "@type": "Organization",
      "name": SITE_NAME,
      "url": SITE_URL
    }
  } : null;

  // Schema.org for Article
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.content?.substring(0, 160),
    "image": article.image_url || DEFAULT_IMAGE,
    "author": {
      "@type": "Organization",
      "name": article.author || SITE_NAME
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.png`
      }
    },
    "datePublished": article.created_at
  } : null;

  // Organization Schema for homepage
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE_NAME,
    "url": SITE_URL,
    "description": metaDescription,
    "sameAs": [
      "https://instagram.com/tikaculture",
      "https://facebook.com/tikaculture",
      "https://twitter.com/tikaculture"
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content="événements, concerts, hip-hop, afrobeat, r&b, dancehall, paris, france, billets, soirées, festivals, musique urbaine, culture" />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content="fr_FR" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@tikaculture" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      
      {/* Additional SEO */}
      <meta name="author" content={SITE_NAME} />
      <meta name="geo.region" content="FR" />
      <meta name="geo.placename" content="Paris" />
      <meta name="language" content="French" />
      
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      {eventSchema && (
        <script type="application/ld+json">
          {JSON.stringify(eventSchema)}
        </script>
      )}
      
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * SEO for Event Details Page
 */
export const EventSEO = ({ event }) => {
  if (!event) return null;
  
  return (
    <SEO
      title={`${event.title} - ${event.artist}`}
      description={`Réservez vos billets pour ${event.title} avec ${event.artist}. ${event.date} à ${event.venue}, ${event.location}. À partir de ${event.price}€.`}
      image={event.image}
      url={`${SITE_URL}/event/${event.id}`}
      type="event"
      event={event}
    />
  );
};

/**
 * SEO for Organizer Page
 */
export const OrganizerSEO = ({ organizer }) => {
  if (!organizer) return null;
  
  return (
    <SEO
      title={organizer.company_name || organizer.name}
      description={organizer.description || `Découvrez les événements de ${organizer.company_name || organizer.name} sur TIKA. Concerts, soirées et festivals.`}
      image={organizer.profile?.banner_url}
      url={`${SITE_URL}/organizer/${organizer.id}`}
      type="profile"
    />
  );
};

/**
 * SEO for Culture/Article Page
 */
export const ArticleSEO = ({ article }) => {
  if (!article) return null;
  
  return (
    <SEO
      title={article.title}
      description={article.content?.substring(0, 160)}
      image={article.image_url}
      url={`${SITE_URL}/culture/${article.id}`}
      type="article"
      article={article}
    />
  );
};
